import {Injectable}                from '@angular/core';
import {NUptakeFactorsStore}       from '../../stores/common/nuptake-factors.store';
import {NuptakeCalculationService} from '../../services/common/nuptake-calculation.service';
import {CropTypeStore}             from '../../stores/base-data/crop.types.store';
import {SatelliteNValues}          from '../../types/ndi.types';
import {GetRoundNumericService}    from '../../ap-utils/service/get-round-numeric.service';
import INdiStatistic = Data.NDI.INdiStatistic;
import IFieldCrop = Data.FieldManagement.IFieldCrop;
import INUptakeFactors = Data.Common.INUptakeFactors;

/**
 * Service for n-index and n-uptake calculations
 */
@Injectable({
  providedIn: 'root'
})
export class NdiService {
  constructor(private cropTypeStore: CropTypeStore,
              private nUptakeFactorsStore: NUptakeFactorsStore,
              private roundNumberService: GetRoundNumericService,
              private nUptakeCalculationService: NuptakeCalculationService) {
  }

  /**
   * Checks if ndi scan statistic is valid.
   * @param ndiStatistic - Ndi statistic object.
   * @returns Boolean indicating whether the ndi scan statistic valid or not.
   * @remarks Status rules:
   * - Currently check only creation date in ndi scan is valid
   * @public
   */
  public isNdiScanValid(ndiStatistic: INdiStatistic | undefined): boolean {
    const createdDate = !!ndiStatistic?.Created ? new Date(ndiStatistic.Created) : undefined;
    return !!createdDate ? createdDate.getFullYear() > 2000 : false;
  }

  /**
   * Calculates satellite N values based on NDI statistics and field crop data
   * @param ndiStatistic NDI statistic object
   * @param fieldCrop Field crop information
   * @returns Object containing calculated N index and uptake values
   * @public
   */
  public getSatelliteNValues(ndiStatistic: INdiStatistic | undefined, fieldCrop: IFieldCrop | undefined): SatelliteNValues {
    const isNdiScanValid = this.isNdiScanValid(ndiStatistic);
    const satelliteNValues = {} as SatelliteNValues;
    if (isNdiScanValid) {
      satelliteNValues.Cells = ndiStatistic.Cells;
      satelliteNValues.NIndexAvg = ndiStatistic.Mean;
      satelliteNValues.NIndexMin = ndiStatistic.Min;
      satelliteNValues.NIndexMax = ndiStatistic.Max;
      const nIndexHistogram = ndiStatistic.Histogram as unknown as { [key: string]: number };
      satelliteNValues.NIndexHistogram = JSON.parse(JSON.stringify(nIndexHistogram));
    }
    if (isNdiScanValid && !!fieldCrop?.CroptypeId && !!fieldCrop?.EcValue) {
      const nUptakeFactor = this.getNUptakeFactor(fieldCrop);
      if (!!nUptakeFactor) {
        satelliteNValues.NUptakeAvg = this.nUptakeCalculationService.calculateNUptakeValue(ndiStatistic.Mean, nUptakeFactor);
        satelliteNValues.NUptakeMin = this.nUptakeCalculationService.calculateNUptakeValue(ndiStatistic.Min, nUptakeFactor);
        satelliteNValues.NUptakeMax = this.nUptakeCalculationService.calculateNUptakeValue(ndiStatistic.Max, nUptakeFactor);
        const nUptakeHistogram = {};
        Object.keys(satelliteNValues.NIndexHistogram).forEach(key => {
          const nUptakeKey = this.nUptakeCalculationService.calculateNUptakeValue(
            +key,
            nUptakeFactor
          );
          const nUptakeRoundedKey = this.roundNumberService.round(nUptakeKey, 1);
          nUptakeHistogram[nUptakeRoundedKey] = satelliteNValues.NIndexHistogram[key];
        });
        satelliteNValues.NUptakeHistogram = nUptakeHistogram;
      }
    }
    return satelliteNValues;
  }

  /**
   * Retrieves N uptake factors for a given field crop
   * @param fieldCrop Field crop information
   * @returns N uptake factors if available, undefined otherwise
   * @public
   */
  public getNUptakeFactor(fieldCrop: IFieldCrop | undefined): INUptakeFactors | undefined {
    if (!fieldCrop) {
      return undefined;
    }
    const mainCrop = this.cropTypeStore.getDefaultCroptype(fieldCrop.CroptypeId);
    return this.nUptakeFactorsStore.getNUptakeFactorByFieldCropAndCropType(fieldCrop, mainCrop);
  }
}
